import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Inbox,
  BarChart3,
  LogOut, 
  Menu, 
  X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    verifyToken();
    fetchStats();
  }, []);

  const verifyToken = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/website/admin');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      localStorage.removeItem('adminToken');
      navigate('/website/admin');
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/website/admin');
  };

  const navItems = [
    { path: '/website/admin/inbox', icon: Inbox, label: 'Gelen Kutusu', testId: 'nav-inbox', badge: stats?.inbox?.unread },
    { path: '/website/admin/stats', icon: BarChart3, label: 'İstatistikler', testId: 'nav-stats' },
  ];

  return (
    <div className="min-h-screen bg-[#080808] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#0d0d0d] border-r border-white/10 transform transition-transform duration-300
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_pay-gaming/artifacts/9msxlx6e_logo_Logo%20Text%20-%20White.png" 
                alt="SOFTMAYA" 
                className="h-8"
              />
            </Link>
            <p className="text-white/40 text-xs mt-2">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative
                    ${isActive 
                      ? 'bg-[#C8AA73]/10 text-[#C8AA73] border border-[#C8AA73]/20' 
                      : 'text-white/60 hover:bg-white/5 hover:text-white'}
                  `}
                  data-testid={item.testId}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {item.badge > 0 && (
                    <span className="absolute right-3 px-2 py-0.5 text-xs rounded-full bg-[#C8AA73] text-black font-medium">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-white/60 hover:text-red-400 hover:bg-red-400/10"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Çıkış
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#080808]/80 backdrop-blur-md border-b border-white/10">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-white/60 hover:text-white"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="flex items-center gap-4 ml-auto">
              <Link to="/" className="text-white/40 hover:text-white text-sm">
                Siteyi Gör →
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet context={{ stats, refreshStats: fetchStats }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
